import React, { useEffect, useState, useCallback } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Loader, useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { MLTagManagerService, MLModalService } from 'ml-portals-services-react';
import { MLQuoteFlowUtil } from 'ml-portals-util-js';
import {MLMultiLobsDataHolderService, MLStateRolloutService} from 'ml-services-common-react';
import { MLAgentChoiceReportService } from 'gw-capability-gateway';
import { Submission } from 'gw-capability-quoteandbind';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { ClauseService } from 'gw-capability-policycommon';

// eslint-disable-next-line import/no-unresolved
import { PECustomQuoteInfoComponent } from 'ml-capability-quoteandbind-common-react';
import { FormModalPopover, platformMessages as mlCommonMessages } from 'ml-components-platform-react';

// import wizardStepToFieldMapping from './config/pa-wizard-step-to-field-mapping.json5';
import wizardConfig from './config/pup-wizard-config.json5';
import messages from './PEPUPWizard.messages';

const PATH_TO_MOCK_DATA = 'quote.pa';
const MOCK_DATA_TO_SET = [
    'baseData.accountHolder.emailAddress1',
    'baseData.accountHolder.accountHolder',
    'baseData.policyAddress.addressLine1',
    'baseData.policyAddress.city',
    'baseData.policyAddress.state',
    'baseData.productCode',
    'baseData.accountHolder.firstName',
    'baseData.accountHolder.lastName',
    'baseData.accountHolder.subtype',
    'baseData.accountHolder.dateOfBirth.day',
    'baseData.accountHolder.dateOfBirth.month',
    'baseData.accountHolder.dateOfBirth.year'
];
const MOCK_DATA_TO_REMOVE = [
    'baseData.accountHolder.emailAddress1',
    'baseData.accountHolder.firstName',
    'baseData.accountHolder.lastName',
    'baseData.accountHolder.dateOfBirth',
    'baseData.policyAddress.addressLine1',
    'baseData.policyAddress.city'
];

function PEPUPWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldSkipValidSteps, setShouldSkipValidSteps] = useState(false);
    const { viewModelService, location, history } = props;
    const [isGroupAccessEligible, setIsGroupAccessEligible] = useState(undefined);

    const { authHeader } = useAuthentication();
    const translator = useTranslator();

    const { showProgressDialog } = MLModalService()
    const { showReportModalWithSubmissionVM } = MLAgentChoiceReportService();
    const { cancel } = MLQuoteFlowUtil();

    useEffect(
        () => {
            const viewModelContext = {
                AccountEmailRequired: false,
                DriverEmailRequired: true,
                AccountDOBRequired: false
            };
            if (!location.state) {
                history.push('/');
                return;
            }

            let submissionPromise;
            if (_.has(location, 'state.submission')) {
                const { submission, resumeFlow } = location.state;
                if (_.get(submission, 'baseData.sameMailingAddress_ML', 'undefined')) {
                    _.set(submission, 'baseData.sameMailingAddress_ML', true);
                }
                if (_.isUndefined(submission.baseData.priorAddressQuestion_Ext)) {
                    _.set(submission, 'baseData.priorAddressQuestion_Ext', false);
                }
                if (resumeFlow){
                    setShouldSkipValidSteps(true);
                }
                submissionPromise = Promise.resolve(submission);
            } else {
                const { quoteentry } = location.state;
                const { postalCode, quoteID } = quoteentry;
                const requestData = {
                    quoteID: quoteID,
                    postalCode: postalCode
                };
                submissionPromise = LoadSaveService.retrieveSubmission(requestData, authHeader);
            }

            submissionPromise.then((response) => {
                MockUpUtil.cleanUpMockedProperties(
                    response,
                    PATH_TO_MOCK_DATA,
                    ...MOCK_DATA_TO_REMOVE
                );

                ClausesUtil.getDependentClausesFromServer(
                    ClauseService,
                    _.get(response, 'quoteID'),
                    authHeader
                );

                const submission = viewModelService.create(
                    new Submission(response),
                    'pc',
                    'edge.capabilities.quote.submission.dto.QuoteDataDTO',
                    viewModelContext
                );
                setInitialSubmission(submission);
                setIsLoading(false);
                return () => {
                    MLTagManagerService.setModel();
                }
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        if (_.has(location, 'state.submission') && isGroupAccessEligible === undefined) {
            const { submission } = location.state;

            MLStateRolloutService.checkIsGroupEnabled(submission?.baseData?.policyAddress?.state, authHeader).then((isGroupEligible) => {
                isGroupEligible = submission?.producerCodeOfRecord_ML?.producerType_ML === "ia" ? false : isGroupEligible;
               
                const isStrategicPartner = localStorage.getItem('strategicPartnershipInd_Ext');

                if (isStrategicPartner?.toLowerCase() === 'y') {
                    setIsGroupAccessEligible(false)
                }
                else {
                    setIsGroupAccessEligible(isGroupEligible);
                }
            });
        }
    }, [authHeader, isGroupAccessEligible, location]);

    const saveSubmissionAndProceed = useCallback((periodStatus, latestSnapshot, nextPath) => {
        return modalApi.showModal(
            <FormModalPopover
                title={commonMessages.wantToCancel}
                simpleMessage={translator(mlCommonMessages.mlInfoWillBeSavedAsQuoteSubmission)}
                actionBtnLabel={commonMessages.yes}
                cancelBtnLabel={mlCommonMessages.mlCancel}
                closeSimpleMessage={false}
            />
        ).then(() => {
            let serverCall = LoadSaveService.updateDraftSubmission;

            if (periodStatus === 'Quoted') {
                serverCall = LoadSaveService.updateQuotedSubmission;
            }

            const wizardDataFromSnapshot = _.get(latestSnapshot, 'value');
            const dataToSave = periodStatus === 'Quoted'
                ? MockUpUtil.setMockData(wizardDataFromSnapshot, PATH_TO_MOCK_DATA)
                : MockUpUtil.setMockData(
                    wizardDataFromSnapshot,
                    PATH_TO_MOCK_DATA,
                    ...MOCK_DATA_TO_SET
                );
            setIsLoading(true);

            const redirectPath = `/quotes/${dataToSave.quoteID}/summary`;
            serverCall(dataToSave, authHeader)
                .then(() => {
                    history.push(nextPath || redirectPath);
                })
                .catch(() => {
                    modalApi.showModal(
                        <FormModalPopover
                            title={messages.anErrorOccurredTitle}
                            simpleMessage={translator(messages.anErrorOccurred)}
                            actionBtnLabel={commonMessages.ok}
                            closeSimpleMessage={false}
                        />
                    ).then(() => {
                        history.push(nextPath || redirectPath);
                    }, _.noop);
                });
            return true;
        }, _.noop);
    }, [authHeader, history, translator]);

    const handleCancel = useCallback(
        (wizardProps) => {
            const { wizardSnapshot, wizardData, currentStepIndex, param: nextPath = '', steps: wizardSteps } = wizardProps;
            const latestSnapshot = wizardData.aspects.subtreeValid && wizardData.aspects.valid
                ? wizardData
                : wizardSnapshot;
            const lastPathName = nextPath.replace(/\/?.*\//g, '');
            const quoteSummaryRegex = /^\/quotes\/[0-9]+\/summary/;
            const skipModal = ['knockoutpage', 'logout', 'contactUs'];
            const lastVisitedStep = MLMultiLobsDataHolderService.getLastVisitedStep();
            const isStatusQuoted = _.get(wizardData, 'baseData.periodStatus.value.code') === 'Quoted';
            if (
                currentStepIndex === wizardSteps.length - 1
                || skipModal.includes(lastPathName)
                || nextPath.match(quoteSummaryRegex)
                || hasErrorOccurred
            ) {
                return true;
            }

            const periodStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code');
            if (periodStatus === 'Bound') {
                // allow transition once the submission is bound
                return true;
            }
            if(nextPath.includes('/contactUs')){
                return true;
            }
            
            // Allow transition to Bundle Cart and Account Summary
            if (nextPath.includes('/agent-bundle-cart') || nextPath.includes('/accounts') || nextPath.includes('/comp-wizard')) {
                return true;
            }

            if (nextPath.includes('/quoteentry')) {
                return true;
            }

            let modalMsg = mlCommonMessages.mlInfoWillBeSavedAsQuoteSubmission;
            if (!wizardData.quoteID.value) {
                modalMsg = mlCommonMessages.mlInfoWillNotBeSaved;
            } else if (!wizardData.quoteData.value) {
                modalMsg =mlCommonMessages.mlInfoSaveAsDraft;
            }

            return modalApi.showModal(
                <FormModalPopover
                    title={translator(mlCommonMessages.areYouSureYouWantToCancel)}
                    simpleMessage={translator(modalMsg)}
                    actionBtnLabel={commonMessages.yes}
                    cancelBtnLabel={mlCommonMessages.mlCancel}
                    closeSimpleMessage={false}
                />
            ).then(() => {
                setIsLoading(true);
                try {
                    const promise = cancel(wizardData, lastVisitedStep, showReportModalWithSubmissionVM, authHeader)
                    showProgressDialog(mlCommonMessages.saveSubmission, promise);
                    history.push(nextPath);
                    return true;
                } catch (error) {
                    history.push(nextPath);
                };
            }, _.noop);
        },
        [authHeader, cancel, hasErrorOccurred, history, modalApi, showProgressDialog, showReportModalWithSubmissionVM, translator]
    );


    const handleError = useCallback(
        (error) => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const state = {
                pathname: '/contactUs',
                state: error
            };

            setHasErrorOccurred(true);
            return <Redirect push to={state} />
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleKnockout = useCallback(({ knockOutErrors }) => {
        history.push('/knockoutpage', { underwritingIssues: knockOutErrors });
     }, [history]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    const submissionStatus = _.get(initialSubmission, 'baseData.status_ML.value');
    const isBindSubmission = !!_.get(initialSubmission, 'bindData.policyNumber.value') && (submissionStatus === 'Bound' || submissionStatus === 'Binding');

    let stepsToShow = steps;

    if (!isGroupAccessEligible) {
        // remove Group Affiliation screen from steps
        stepsToShow = steps.filter((step) => step.mlStepName !== 'pupGroupAssociations');
    }

    return (
        <QuoteInfoComponentContext.Provider value={PECustomQuoteInfoComponent}>
            <ErrorBoundary onError={handleError}>
                <Wizard
                    initialSteps={isBindSubmission ? [stepsToShow[stepsToShow.length -1]] : stepsToShow}
                    wizardTitle={commonMessages.wantToCancel}
                    initialData={initialSubmission}
                    onKnockOut={handleKnockout}
                    onCancel={handleCancel}
                    skipCompletedSteps={shouldSkipValidSteps}
                    // wizardStepToFieldMapping={wizardStepToFieldMapping}
                />
            </ErrorBoundary>
        </QuoteInfoComponentContext.Provider>
    );
}

PEPUPWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            quoteentry: PropTypes.shape({
                postalCode: PropTypes.string,
                quoteID: PropTypes.string
            })
        })
    }).isRequired
};

export default withRouter(withViewModelService(PEPUPWizard));